import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Coupon, CouponTemplate } from '../types/coupon';
import { formatDateInBusinessTZ } from '../utils/date';
import { getErrorMessage } from '../utils/errorMessage';
import { Tag, Percent, DollarSign, Calendar, Hash, User, ChevronLeft } from 'lucide-react';

interface CouponWithTemplate extends Coupon {
  template: CouponTemplate | null;
}

export function Coupons() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  // Fetch parent id
  const { data: parent } = useQuery({
    queryKey: ['parent-id', user?.email],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user!.email!)
        .single();
      if (error) throw error;
      return data as { id: string };
    },
    enabled: !!user?.email
  });

  // Fetch coupons for the parent
  const { data: coupons, isLoading, error } = useQuery<CouponWithTemplate[]>({
    queryKey: ['parent-coupons', parent?.id],
    queryFn: async () => {
      if (!parent?.id) return [];
      
      const { data, error } = await supabase
        .from('coupons')
        .select(`
          *,
          template:coupon_templates(*)
        `)
        .eq('user_id', parent.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as CouponWithTemplate[];
    },
    enabled: !!parent?.id
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error loading coupons: {getErrorMessage(error)}</p>
          </div>
        </div>
      </div>
    );
  }

  const formatCouponValue = (coupon: CouponWithTemplate) => {
    if (coupon.type === 'percentage') {
      return `${coupon.value}%`;
    }
    return `$${coupon.value.toFixed(2)}`;
  };

  const getCouponStatus = (coupon: CouponWithTemplate) => {
    if (coupon.expires_at && new Date(coupon.expires_at) < new Date()) {
      return { status: 'expired', color: 'text-red-600', bgColor: 'bg-red-100' };
    }
    if (coupon.usage_limit && coupon.used_count >= coupon.usage_limit) {
      return { status: 'fully used', color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }
    if (coupon.applicable_invoice_id) {
      return { status: 'applied to invoice', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    }
    return { status: 'available', color: 'text-green-600', bgColor: 'bg-green-100' };
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Classic iPhone-style Back Button */}
        <button
          onClick={handleGoBack}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </button>
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Coupons</h1>
          <p className="text-gray-600 mt-2">
            View all available coupons and discounts for your account
          </p>
        </div>

        {/* Coupons List */}
        {coupons && coupons.length > 0 ? (
          <div className="space-y-4">
            {coupons.map((coupon) => {
              const status = getCouponStatus(coupon);
              return (
                <div
                  key={coupon.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Coupon Header */}
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="flex items-center space-x-2">
                          {coupon.type === 'percentage' ? (
                            <Percent className="w-5 h-5 text-blue-600" />
                          ) : (
                            <DollarSign className="w-5 h-5 text-green-600" />
                          )}
                          <span className="text-lg font-semibold text-gray-900">
                            {formatCouponValue(coupon)}
                          </span>
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                          {status.status}
                        </span>
                      </div>

                      {/* Coupon Code */}
                      <div className="flex items-center space-x-2 mb-3">
                        <Hash className="w-4 h-4 text-gray-400" />
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">
                          {coupon.code}
                        </code>
                      </div>

                      {/* Template Description */}
                      {coupon.template?.description && (
                        <p className="text-gray-700 mb-3">{coupon.template.description}</p>
                      )}

                      {/* Coupon Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            Created: {formatDateInBusinessTZ(coupon.created_at)}
                          </span>
                        </div>
                        
                        {coupon.expires_at && (
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4" />
                            <span>
                              Expires: {formatDateInBusinessTZ(coupon.expires_at)}
                            </span>
                          </div>
                        )}

                        {coupon.usage_limit && (
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>
                              Usage: {coupon.used_count} / {coupon.usage_limit}
                            </span>
                          </div>
                        )}

                        {!coupon.usage_limit && (
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4" />
                            <span>
                              Usage: {coupon.used_count} / Unlimited
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Tag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No coupons available</h3>
            <p className="text-gray-600">
              You don't have any coupons at the moment. Coupons will appear here when they're assigned to your account.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
